import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import {
  defaultReqsRequestComplete,
  defaultRequisitions,
  providerProfileState,
} from "../../recoil/atoms";
import { UserAuthProviderResponse } from "../../recoil/types";
import { RefreshRequisitionRows } from "../../components/Grid/Services/GridService";
import { GetNewProviderInfoFromToken } from "../../components/Login/Services/LoginService";
import { RefreshProviderProfile } from "../../components/Profile/Services/UpdateProfileInfoService";
import { useAuthenticationHook } from "./useAuthenticationHook";
import { setCookieAccessToken } from "../requestUtils";

interface UseCreateProviderSessionOptions {
  getAccessTokenSilently: () => Promise<string>;
  isAuthenticated: boolean;
  isLoading: boolean;
  onError: (message: string) => void;
}

interface UseCreateProviderSessionResult {
  isProviderSessionReady: boolean;
}

export const useCreateProviderSession = ({
  getAccessTokenSilently,
  isAuthenticated,
  isLoading,
  onError,
}: UseCreateProviderSessionOptions): UseCreateProviderSessionResult => {
  const [hasCreatedProviderSession, setHasCreatedProviderSession] =
    useState(false);
  const [, setRequisitions] = useRecoilState(defaultRequisitions);
  const [, setDefaultReqsComplete] = useRecoilState(defaultReqsRequestComplete);
  const [, setProviderProfile] = useRecoilState(providerProfileState);
  const {
    auth,
    loggedOutInactivityFlag,
    setLoggedOut,
    setLoggedOutInactivityFlag,
    setProviderAuth,
  } = useAuthenticationHook();

  useEffect(() => {
    if (isLoading || !isAuthenticated || auth?.token) {
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    const createProviderSession = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        signal.throwIfAborted();

        const refreshRequisitionRowsPromise = RefreshRequisitionRows(
          accessToken,
          (data) => {
            if (!signal.aborted) setRequisitions(data);
          },
          setLoggedOut,
          undefined,
          (val) => {
            if (!signal.aborted) setDefaultReqsComplete(val);
          },
        ).catch((err) => {
          console.error("Failed to refresh requisition rows after login", err);
        });

        const response = await GetNewProviderInfoFromToken(
          accessToken,
          setLoggedOut,
        );
        signal.throwIfAborted();

        if (!response.ok) {
          throw new Error(
            `Provider session creation failed: ${response.status}`,
          );
        }

        const userData = (await response.json()) as UserAuthProviderResponse;
        signal.throwIfAborted();

        setCookieAccessToken(userData.token);
        await RefreshProviderProfile(
          userData.token,
          setProviderProfile,
          setLoggedOut,
        );
        signal.throwIfAborted();
        await refreshRequisitionRowsPromise;
        signal.throwIfAborted();

        if (loggedOutInactivityFlag) {
          setLoggedOutInactivityFlag(false);
        }
        setProviderAuth(userData);
        setHasCreatedProviderSession(true);
      } catch (error) {
        if (signal.aborted) return;

        controller.abort();
        setLoggedOut(false);
        onError("Please try again.");
        console.error("Failed to create provider session", error);
      }
    };

    createProviderSession();

    return () => {
      controller.abort();
    };
  }, [
    auth?.token,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loggedOutInactivityFlag,
    onError,
    setDefaultReqsComplete,
    setLoggedOut,
    setLoggedOutInactivityFlag,
    setProviderProfile,
    setProviderAuth,
    setRequisitions,
  ]);

  return {
    isProviderSessionReady: hasCreatedProviderSession && Boolean(auth?.token),
  };
};
